import { useIntl } from 'react-intl';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  NativeSelect,
  Stack
} from '@mui/material';
import PropTypes from 'prop-types';
import Checkbox from '@mui/material/Checkbox';
import { useGetAiModelsByProviderIdQuery, useGetAiProvidersQuery } from 'services/aiProviderApi';
import { useGetScriptsQuery } from 'services/scriptApi';

export function GroovyNodeForm({ id, data, onUpdateData }) {
  const { formatMessage: msg } = useIntl();
  const { providerCode, modelCode, isAsynch, scriptCode } = data;

  // Fetch providers from API
  const { data: providers = [], isLoading: providersLoading } = useGetAiProvidersQuery();
  const { data: scripts = [], isLoading: scriptLoading } = useGetScriptsQuery();

  const { control, watch } = useForm({
    defaultValues: {
      scriptCode: scriptCode || '',
      providerCode: providerCode || '',
      modelCode: modelCode || '',
      isAsynch: isAsynch ?? false
    }
  });

  const formValues = watch();

  const filteredProviders = providers.filter((p) => p.displayableKey !== '***********');
  const selectedProvider =
    filteredProviders.find((p) => p.code === formValues.providerCode) ?? filteredProviders[0];
  const selectedProviderId = selectedProvider?.id;

  const { data: models = [], isLoading: modelsLoading } = useGetAiModelsByProviderIdQuery(
    selectedProviderId,
    { skip: !selectedProviderId }
  );

  // Sort models: favorites first, then alphabetically by label
  const sortedModels = [...models].sort((a, b) => {
    if (a.favorite && !b.favorite) return -1;
    if (!a.favorite && b.favorite) return 1;

    return a.label.localeCompare(b.label);
  });

  useEffect(() => {
    const updatedData = {
      ...data,
      scriptCode: formValues.scriptCode,
      providerCode: formValues.providerCode === '-1' ? '' : formValues.providerCode,
      modelCode: formValues.modelCode === '-1' ? '' : formValues.modelCode,
      isAsynch: formValues.isAsynch
    };

    onUpdateData?.({
      id,
      data: updatedData
    });
  }, [formValues, data, onUpdateData, id]);

  return (
    <Stack spacing={1} sx={{ p: 1 }}>
      <Controller
        name="scriptCode"
        control={control}
        rules={{ required: msg({ id: 'field.script.name.required' }) }}
        render={({ field }) => (
          <FormControl fullWidth>
            <InputLabel variant="standard" htmlFor="provider-native-select">
              {msg({ id: 'field.script.name.label' })}
            </InputLabel>
            {scriptLoading ? (
              <CircularProgress size={24} />
            ) : (
              <NativeSelect
                {...field}
                label={msg({ id: 'field.script.name.label' })}
                required
                inputProps={{
                  id: 'provider-native-select',
                  name: 'scriptCode'
                }}
              >
                <option value="-1">Sélectionner un script</option>
                {scripts.map((provider) => (
                  <option key={provider.code} value={provider.code}>
                    {provider.name}
                  </option>
                ))}
              </NativeSelect>
            )}
          </FormControl>
        )}
      />

      <Controller
        name="providerCode"
        control={control}
        rules={{ required: msg({ id: 'field.provider.required' }) }}
        render={({ field }) => (
          <FormControl fullWidth>
            <InputLabel variant="standard" htmlFor="provider-native-select">
              {msg({ id: 'field.provider.label' })}
            </InputLabel>
            {providersLoading ? (
              <CircularProgress size={24} />
            ) : (
              <NativeSelect
                {...field}
                label={msg({ id: 'field.provider.label' })}
                required
                inputProps={{
                  id: 'provider-native-select',
                  name: 'providerCode'
                }}
              >
                <option value="-1">Sélectionner un fournisseur</option>
                {filteredProviders.map((provider) => (
                  <option key={provider.code} value={provider.code}>
                    {provider.label}
                  </option>
                ))}
              </NativeSelect>
            )}
          </FormControl>
        )}
      />
      <Controller
        name="modelCode"
        control={control}
        rules={{ required: msg({ id: 'field.model.required' }) }}
        render={({ field }) => (
          <FormControl fullWidth>
            <InputLabel variant="standard" htmlFor="model-native-select">
              {msg({ id: 'field.model.label' })}
            </InputLabel>
            {modelsLoading ? (
              <CircularProgress size={24} />
            ) : (
              <NativeSelect
                {...field}
                label={msg({ id: 'field.model.label' })}
                required
                disabled={!selectedProviderId}
                inputProps={{
                  id: 'model-native-select',
                  name: 'modelCode'
                }}
              >
                <option value="0">{msg({ id: 'field.model.default.label' })}</option>
                {sortedModels.map((model) => (
                  <option key={model.code} value={model.code}>
                    {model.label}
                  </option>
                ))}
              </NativeSelect>
            )}
          </FormControl>
        )}
      />

      <Controller
        name="isAsynch"
        control={control}
        render={({ field: { onChange, value, ...field } }) => (
          <FormControlLabel
            control={<Checkbox onChange={onChange} checked={value} {...field} />}
            label={msg({ id: 'field.script.synch.label' })}
          />
        )}
      />
    </Stack>
  );
}

GroovyNodeForm.propTypes = {
  id: PropTypes.string,
  data: PropTypes.shape({
    id: PropTypes.number,
    scriptCode: PropTypes.string,
    providerCode: PropTypes.string,
    modelCode: PropTypes.string,
    isAsynch: PropTypes.bool
  }),
  onUpdateData: PropTypes.func.isRequired
};
